/**
 * Use this function after you've already transformed the backend errors to InjectedErrors
 *
 * You'll have an object that will have {'field': errors} interface
 *
 * In some cases, after this initial transformation, if some field has a nested error inside such as
 *
 * {'field.0': errors}, you might need to merge all those "nested" indexed keys to the parent such as
 *
 * {'field'} : errors} where all the errors will be merged into 'field'
 */
export const mergeNestedKeyErrorsToParentKey = (parentKey, errorsToBeInjected) => {
    return Object.entries(errorsToBeInjected).reduce((accum, [key, value]) => {
        const hasKey = Boolean(accum[parentKey]);
        // If parent key doesn't have any errors in errorsToBeInjected,
        // create the key-value pair
        if (!hasKey) {
            accum[parentKey] = { ...value, errors: [] };
        }
        // current error's key starts with the passed parentKey
        const shouldBeGrouped = key.indexOf(parentKey) === 0;
        // if errors should be combined, spread old and current errors in
        // the 'parentKey' InjectedError's error array
        if (shouldBeGrouped &&
            accum[parentKey] &&
            accum[parentKey]?.errors) {
            accum[parentKey].errors = [
                ...accum[parentKey]['errors'],
                ...value.errors,
            ];
        }
        else {
            accum[key] = value;
        }
        return accum;
    }, {});
};
/**
 *
 * Use this function to transform server errors to InjectableErrors for use
 * with `injectErrors` method out of `useFormRoot` hook in case you need to
 * inject the errors in `FormRoot`'s context to propagate down to individual fields.
 *
 * @param errors - errors coming from the BE / remapped ZOD Validation with safeParse
 * @param override - `boolean` - default `true` - should it set `override` property of the newly created InjectedError to `true`
 */
export const transformServerErrorsToInjectableErrors = (errors, override = true) => {
    return errors.reduce((accum, e) => {
        return {
            ...accum,
            [e.path.join('.')]: {
                errors: [{ ...e, isServerError: true }],
                override,
            },
        };
    }, {});
};
/**
 * Use with errors returned from useFormRoot hook to filter out server errors and leave only
 * validation errors / other injected errors which don't have isServerError property or it is set to false.
 *
 */
export const filterOutServerErrors = (errors) => {
    const filteredErrors = {};
    // Need to give key a type before iterating - TS issue
    let key;
    for (key in errors) {
        const filtered = errors[key].filter((error) => !error.isServerError);
        if (filtered.length) {
            filteredErrors[key] = filtered;
        }
    }
    return filteredErrors;
};
/**
 * Use with errors returned from useFormRoot hook to filter out validation / injected errors and leave only
 * backend errors with isServerError set to true.
 *
 */
export const filterOutValidationErrors = (errors) => {
    const filteredErrors = {};
    // Need to give key a type before iterating - TS issue
    let key;
    for (key in errors) {
        const filtered = errors[key].filter((error) => error.isServerError);
        if (filtered.length) {
            filteredErrors[key] = filtered;
        }
    }
    return filteredErrors;
};
